import { useRef } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useMotionTemplate,
} from "framer-motion";
import { LOGO_URL } from "../data";

const Chip = () => (
  <svg
    className="w-12 h-9 md:w-[58px] md:h-[42px]"
    viewBox="0 0 58 42"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="gold" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#fbeeba" />
        <stop offset="0.45" stopColor="#c9a13f" />
        <stop offset="0.7" stopColor="#9c7a26" />
        <stop offset="1" stopColor="#f3e0a3" />
      </linearGradient>
    </defs>
    <rect x="0.5" y="0.5" width="57" height="41" rx="7" fill="url(#gold)" />
    <g stroke="rgba(90,66,12,0.55)" strokeWidth="1.1">
      <line x1="0" y1="14" x2="20" y2="14" />
      <line x1="38" y1="14" x2="58" y2="14" />
      <line x1="0" y1="28" x2="20" y2="28" />
      <line x1="38" y1="28" x2="58" y2="28" />
      <line x1="20" y1="0" x2="20" y2="42" />
      <line x1="38" y1="0" x2="38" y2="42" />
      <rect x="20" y="14" width="18" height="14" rx="2" />
    </g>
  </svg>
);

const Contactless = () => (
  <svg
    className="w-6 h-6 md:w-7 md:h-7"
    viewBox="0 0 24 24"
    fill="none"
    stroke="rgba(255,255,255,0.55)"
    strokeWidth="1.6"
    strokeLinecap="round"
  >
    <path d="M8 6a10 10 0 0 1 0 12" />
    <path d="M12 4a14 14 0 0 1 0 16" />
    <path d="M16 2.5a18 18 0 0 1 0 19" />
  </svg>
);

// A luxurious, realistic metal card with pointer parallax, specular glare + idle float.
export const StraightCard = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);

  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [13, -13]), {
    stiffness: 120,
    damping: 18,
  });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-16, 16]), {
    stiffness: 120,
    damping: 18,
  });

  const glareX = useTransform(mx, [-0.5, 0.5], [15, 85]);
  const glareY = useTransform(my, [-0.5, 0.5], [5, 75]);
  const glare = useMotionTemplate`radial-gradient(600px circle at ${glareX}% ${glareY}%, rgba(255,255,255,0.28), rgba(255,255,255,0.04) 32%, transparent 55%)`;

  const handleMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  const reset = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={reset}
      style={{ perspective: 1600 }}
      className="w-full flex items-center justify-center"
      data-testid="hero-3d-card"
    >
      <div style={{ animation: "float-card 6.5s ease-in-out infinite" }}>
        <motion.div
          style={{
            rotateX,
            rotateY,
            transformStyle: "preserve-3d",
            boxShadow:
              "0 40px 80px -30px rgba(0,0,0,0.55), 0 12px 30px -12px rgba(0,0,0,0.4)",
          }}
          className="relative w-[320px] h-[202px] md:w-[440px] md:h-[277px] rounded-[24px] p-7 md:p-8 flex flex-col justify-between"
        >
          {/* ---- Card body (brushed obsidian metal) ---- */}
          <div
            className="absolute inset-0 rounded-[24px] overflow-hidden"
            style={{
              background:
                "linear-gradient(135deg, #33363f 0%, #191c23 34%, #0a0b0f 66%, #22252d 100%)",
              border: "1px solid rgba(255,255,255,0.18)",
              boxShadow: "inset 0 1px 1px rgba(255,255,255,0.22)",
            }}
          >
            {/* brushed-metal micro striations */}
            <div
              className="absolute inset-0 opacity-[0.10]"
              style={{
                background:
                  "repeating-linear-gradient(115deg, rgba(255,255,255,0.9) 0px, rgba(255,255,255,0) 2px, rgba(255,255,255,0) 5px)",
              }}
            />
            {/* deep vignette for material depth */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(120% 120% at 80% 0%, rgba(255,255,255,0.06), transparent 45%), radial-gradient(140% 140% at 0% 100%, rgba(0,0,0,0.55), transparent 55%)",
              }}
            />
            {/* iridescent holo band */}
            <div
              className="absolute -bottom-6 -right-10 w-64 h-64 opacity-30 blur-2xl"
              style={{
                background:
                  "conic-gradient(from 210deg, #3b3df2, #7a5cff, #d9c37a, #3b3df2)",
              }}
            />
            {/* accent glow */}
            <div
              className="absolute -top-1/4 -right-1/4 w-[130%] h-[130%] opacity-25"
              style={{
                background:
                  "radial-gradient(closest-side, rgba(59,61,242,0.55), transparent 70%)",
              }}
            />
            {/* pointer-tracking specular glare */}
            <motion.div
              className="absolute inset-0"
              style={{ background: glare, mixBlendMode: "screen" }}
            />
            {/* fine grain */}
            <div className="absolute inset-0 noise-overlay" />
          </div>

          {/* ---- Top row: label + contactless ---- */}
          <div
            className="relative flex items-start justify-between"
            style={{ transform: "translateZ(55px)" }}
          >
            <span className="font-mono-str text-[11px] md:text-sm uppercase tracking-[0.28em] text-white/75">
              Stablecoins Card
            </span>
            <Contactless />
          </div>

          {/* ---- Middle: chip ---- */}
          <div className="relative" style={{ transform: "translateZ(45px)" }}>
            <Chip />
          </div>

          {/* ---- Bottom row: Straight logo ---- */}
          <div
            className="relative flex items-end justify-end"
            style={{ transform: "translateZ(50px)" }}
          >
            <img
              src={LOGO_URL}
              alt="Straight"
              className="h-6 md:h-8 w-auto opacity-95 [filter:invert(1)_drop-shadow(0_1px_1px_rgba(0,0,0,0.4))]"
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
};
